import type { FC } from 'react';
import { useMemo, useCallback } from 'react';
import { PapiGallection } from '@wix/da-papi-types';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import OnScrollBottom, { DISTANCE_UNIT } from '@wix/da-shared-react/pkg/OnScrollBottom';
import DndSortableList from '@wix/da-shared-react/pkg/Dnd/DndSortableList';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import { BiData, BiContextProvidedValue, ItemClickBiEvent } from '@wix/da-bi/pkg/events';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import useBookmarksStream from '../../../hooks/useBookmarksStream';
import FolderCard from '../../_partials/FolderCard';
import { THUMB_WIDTH, THUMB_HEIGHT, THUMB_GAP } from './constants';

import s from './BookmarksListMobile.scss';

export interface Props {
  withFolderActions?: boolean;
  withHeader?: boolean;
  withDragToOrder?: boolean;
  expandedByDefault?: boolean;
  updateFolderOrder: (folder: PapiGallection, position: number) => void;
}

const BookmarksListMobile: FC<Props> = ({
  withFolderActions,
  withDragToOrder,
  updateFolderOrder,
}) => {
  const { items, hasMore, isFetching, fetchNext } = useBookmarksStream();
  const { ref, dimensions: measuredContainer } = useMeasureElementCookie(
    MeasuredCookieType.STASH_FOLDER_CARD,
    { cookieDim: 'width' }
  );

  const visibleBookmarks = useMemo(() => (items ?? []).filter(f => !f.isHidden), [items]);

  const thumbWidth = measuredContainer?.width
    ? (measuredContainer.width - THUMB_GAP) / 2
    : THUMB_WIDTH;

  const onFolderDragOrder = useCallback(
    (item, position) => updateFolderOrder(item.folder, position),
    [updateFolderOrder]
  );

  const sortableItems = useMemo(
    () => items.map((folder: PapiGallection) => ({ id: folder.folderId, folder })),
    [items]
  );

  const renderItem = useCallback(
    (folder: PapiGallection) => (
      <FolderCard
        key={`folder-${folder.folderId}`}
        folder={folder}
        width={thumbWidth}
        height={THUMB_HEIGHT}
        withActions={withFolderActions}
        biData={BiData<ItemClickBiEvent>({
          evid: 100,
          view: BiContextProvidedValue,
          component: BiContextProvidedValue,
          page_typeid: BiContextProvidedValue,
          page_itemid: BiContextProvidedValue,
          sectionname: BiContextProvidedValue,
          typeid: gallectionTypeToResourceType(folder.type),
          itemid: folder.folderId,
        })}
      />
    ),
    [withFolderActions, thumbWidth]
  );
  return (
    <div className={s['root']} ref={ref}>
      <div className={s['grid']}>
        {withDragToOrder && (
          <DndSortableList
            key="folder-cards"
            dndType={DraggableType.STUDIO_BOOKMARK}
            items={sortableItems}
            onDragOrder={onFolderDragOrder}
          >
            {item => renderItem(item.folder)}
          </DndSortableList>
        )}
        {!withDragToOrder && visibleBookmarks.map(renderItem)}
      </div>

      {hasMore && !isFetching ? (
        <OnScrollBottom
          checkOnMount
          checkOnUpdate={hasMore}
          onScrolledToBottom={() => fetchNext()}
          distanceToBottom={100}
          distanceUnit={DISTANCE_UNIT.PX}
        />
      ) : null}
    </div>
  );
};
BookmarksListMobile.displayName = 'BookmarksListMobile';

export default BookmarksListMobile;
